import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Typography,
  Tab,
  Tabs,
} from "@mui/material";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const exportColumns = [
  ["fee_concession_name", "Fee Concession Name"],
  ["no_of_students", "No. of Students"],
  ["amount", "Amount"],
  ["workflow_status", "Status"],
];

export default function FeeConcessionApproval() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const modifiedBy = user?.user_email || user?.username || "";
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selection, setSelection] = useState([]);
  const [approving, setApproving] = useState(false);
  const [exportMenu, setExportMenu] = useState({ anchorEl: null, rows: [], label: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [proofOpen, setProofOpen] = useState(false);
  const [proofUrl, setProofUrl] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };
  const handleOpenProof = (url) => {
    setProofUrl(url);
    setProofOpen(true);
  };

  const handleCloseProof = () => {
    setProofOpen(false);
    setProofUrl("");
  };
  const showMessage = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const fetchGroups = useCallback(async () => {
    if (!clientId || !collegeId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/fee-concessions/${clientId}/${collegeId}/approval-groups/list`
      );
      setGroups(
        (data?.data || []).map((group, index) => ({
          ...group,
          id: `${group.concession_type_id || "general"}-${group.workflow_status}-${index}`,
        }))
      );
    } catch (error) {
      showMessage(
        error?.response?.data?.message || "Unable to load Fee Concession approvals",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, clientId, collegeId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const openStudents = (group) => {
    setSelectedGroup(group);
    setSelection(group.students.map((student) => String(student.fee_concession_id)));
  };

  const declineSelected = async () => {
    if (!selection.length) return showMessage("Select at least one student", "warning");
    setApproving(true);
    try {
      const { data } = await axios.patch(
        `${API_BASE_URL}/api/fee-concessions/${clientId}/${collegeId}/approval-groups/decline`,
        { fee_concession_ids: selection, modified_by: modifiedBy }
      );
      showMessage(data?.message || "Selected Fee Concessions declined successfully");
      setSelectedGroup(null);
      setSelection([]);
      await fetchGroups();
    } catch (error) {
      showMessage(
        error?.response?.data?.message || "Unable to decline selected concessions",
        "error"
      );
    } finally {
      setApproving(false);
    }
  };

  const approveSelected = async () => {
    if (!selection.length) return showMessage("Select at least one student", "warning");
    setApproving(true);
    try {
      const { data } = await axios.patch(
        `${API_BASE_URL}/api/fee-concessions/${clientId}/${collegeId}/approval-groups/approve`,
        { fee_concession_ids: selection, modified_by: modifiedBy }
      );
      showMessage(data?.message || "Selected Fee Concessions approved successfully");
      setSelectedGroup(null);
      setSelection([]);
      await fetchGroups();
    } catch (error) {
      showMessage(
        error?.response?.data?.message || "Unable to approve selected concessions",
        "error"
      );
    } finally {
      setApproving(false);
    }
  };

  const exportExcel = (rows, label) => {
    const data = rows.map((row) =>
      Object.fromEntries(
        exportColumns.map(([field, title]) => [
          title,
          field === "amount" ? Number(row[field] || 0).toFixed(2) : row[field] ?? "",
        ])
      )
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), label);
    XLSX.writeFile(workbook, `Fee_Concession_${label}.xlsx`);
  };
  const handleClose = () => {
    setOpen(false);
    setSelectedGroup(null);
  };
  const exportPdf = (rows, label) => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.text(`Fee Concession ${label}`, 14, 12);
    autoTable(doc, {
      startY: 18,
      head: [exportColumns.map(([, title]) => title)],
      body: rows.map((row) =>
        exportColumns.map(([field]) =>
          field === "amount" ? Number(row[field] || 0).toFixed(2) : row[field] ?? ""
        )
      ),
      headStyles: { fillColor: [25, 118, 210] },
    });
    doc.save(`Fee_Concession_${label}.pdf`);
  };

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 80,
        sortable: false,
        valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
      },
      { field: "fee_concession_name", headerName: "Fee Concession Name", minWidth: 280, flex: 1 },
      { field: "no_of_students", headerName: "No. of Students", width: 160 },
      {
        field: "amount",
        headerName: "Amount",
        width: 150,
        valueFormatter: ({ value }) => Number(value || 0).toFixed(2),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <Button
            size="medium"
            variant="contained"
            aria-label={`View students for ${row.fee_concession_name}`}
            startIcon={<VisibilityIcon />}
            onClick={() => openStudents(row)}
            sx={{ color: "#fff" }}
          >
            View
          </Button>
        ),
      },
    ],
    []
  );

  const studentColumns = [
    { field: "student_rollno", headerName: "Student No.", minWidth: 140, flex: 0.7 },
    { field: "student_name", headerName: "Student Name", minWidth: 210, flex: 1 },
    {
      field: "concession_date",
      headerName: "Date",
      width: 120,
      valueFormatter: ({ value }) => (value ? new Date(value).toLocaleDateString("en-IN") : ""),
    },
    {
      field: "fee_amount",
      headerName: "Amount",
      width: 120,
      valueFormatter: ({ value }) => Number(value || 0).toFixed(2),
    },
    {
      field: "concession_proof",
      headerName: "Proof",
      minWidth: 150,
      flex: 0.7,
      renderCell: ({ value }) => {
        if (!value) return "-";

        const url = /^https?:\/\//i.test(value)
          ? value
          : `${API_BASE_URL}${String(value).startsWith("/") ? "" : "/"}${value}`;

        return (
          <Button
            size="medium"
            variant="contained"
            color="info"
            onClick={() => handleOpenProof(url)}
          >
            View Proof
          </Button>
        );
      },
    },
    { field: "remarks", headerName: "Remarks", minWidth: 180, flex: 0.8 },
  ];

  const renderSection = (title, rows) => (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" mb={1}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {title} ({rows.length})
        </Typography>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mr: 2 }}>
          Total Amount: {rows.reduce((sum, row) => sum + Number(row.amount || 0), 0).toFixed(2)}
        </Typography>
        <Button
          variant="contained"
          startIcon={<MoreVertIcon />}
          disabled={!rows.length}
          sx={{ color: "#fff" }}
          onClick={(event) =>
            setExportMenu({
              anchorEl: event.currentTarget,
              rows,
              label: title.startsWith("Pending") ? "Draft" : "Approved",
            })
          }
        >
          Actions
        </Button>
      </Stack>
      <Box sx={{ height: 410, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSize={pageSize}
          page={page}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          pagination
          disableSelectionOnClick
        />
        <Dialog open={proofOpen} onClose={handleCloseProof} maxWidth="lg" fullWidth>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Concession Proof
            <IconButton onClick={handleCloseProof}>
              <CloseIcon color="error" />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {proofUrl?.toLowerCase().endsWith(".pdf") ? (
              <iframe
                src={proofUrl}
                title="Proof"
                width="100%"
                height="700px"
                style={{ border: "none" }}
              />
            ) : (
              <img
                src={proofUrl}
                alt="Proof"
                style={{
                  width: "100%",
                  maxHeight: "75vh",
                  objectFit: "contain",
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </Paper>
  );

  const draftGroups = groups.filter((group) => group.workflow_status === "DRAFT");
  const approvedGroups = groups.filter((group) => group.workflow_status === "APPROVED");

  return (
    <Box p={2}>
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Typography variant="h5" sx={{ flexGrow: 1 }}>
              APPROVE FEE CONCESSIONS
            </Typography>
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 66,
              mb: 2,
              borderBottom: "1px solid #e0e0e0",

              "& .MuiTabs-indicator": {
                height: 4,
                borderRadius: "4px 4px 0 0",
                backgroundColor: "#455a64",
              },

              "& .MuiTab-root": {
                minHeight: 62,
                minWidth: { xs: 175, sm: 235 },
                px: 2.5,
                mr: 1,
                borderRadius: "8px 8px 0 0",
                fontSize: { xs: 13, sm: 15 },
                fontWeight: 700,
                textTransform: "none",
                opacity: 1,
              },

              /* Pending - Light Orange */
              "& .MuiTab-root:nth-of-type(1)": {
                backgroundColor: "#FFF3E0",
                color: "#9A5300",
                "&:hover": {
                  backgroundColor: "#FFE0B2",
                },
              },

              /* Approved - Light Green */
              "& .MuiTab-root:nth-of-type(2)": {
                backgroundColor: "#E8F5E9",
                color: "#1B5E20",
                "&:hover": {
                  backgroundColor: "#C8E6C9",
                },
              },

              "& .MuiTab-root.Mui-selected": {
                fontWeight: 800,
                boxShadow: "inset 0 -4px 0 #455a64",
              },
            }}
          >
            <Tab label={`Pending (${draftGroups.length})`} />
            <Tab label={`Approved (${approvedGroups.length})`} />
          </Tabs>

          {activeTab === 0 && renderSection("Pending Fee Concession Approvals", draftGroups)}

          {activeTab === 1 && renderSection("Approved Fee Concessions", approvedGroups)}
        </Paper>
      </Stack>

      <Dialog
        open={Boolean(selectedGroup)}
        onClose={() => setSelectedGroup(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography variant="h6">{selectedGroup?.fee_concession_name} - Students</Typography>

            <Typography variant="body2">
              Total Students: {selectedGroup?.no_of_students || 0} | Total Amount:{" "}
              {Number(selectedGroup?.amount || 0).toFixed(2)}
            </Typography>
          </Box>

          <IconButton
            onClick={() => setSelectedGroup(null)}
            size="medium"
            sx={{ color: "error.main" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 470, width: "100%", mt: 1 }}>
            <DataGrid
              rows={(selectedGroup?.students || []).map((row) => ({
                ...row,
                id: String(row.fee_concession_id),
              }))}
              columns={studentColumns}
              checkboxSelection
              selectionModel={selection}
              onSelectionModelChange={(ids) => setSelection(ids.map(String))}
              disableSelectionOnClick
              pageSize={pageSize}
              page={page}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={handlePageSizeChange}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              pagination
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {selectedGroup?.workflow_status === "DRAFT" && (
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              disabled={!selection.length || approving}
              onClick={approveSelected}
              sx={{
                backgroundColor: "#1976d2",
                color: "#fff",
                "&:hover": { backgroundColor: "#115293" },
              }}
            >
              Approve Selected ({selection.length})
            </Button>
          )}
          {selectedGroup?.workflow_status === "APPROVED" && (
            <Button
              variant="contained"
              startIcon={<CancelIcon />}
              disabled={!selection.length || approving}
              onClick={declineSelected}
              sx={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                "&:hover": { backgroundColor: "#b71c1c" },
              }}
            >
              Decline Selected ({selection.length})
            </Button>
          )}
          <Button
            onClick={() => {
              setSelectedGroup(null);
              setSelection([]);
            }}
            variant="contained"
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={exportMenu.anchorEl}
        open={Boolean(exportMenu.anchorEl)}
        onClose={() => setExportMenu({ anchorEl: null, rows: [], label: "" })}
      >
        <MenuItem disableRipple>
          <Button
            fullWidth
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            sx={{ backgroundColor: "#d32f2f", color: "#fff" }}
            onClick={() => {
              exportPdf(exportMenu.rows, exportMenu.label);
              setExportMenu({ anchorEl: null, rows: [], label: "" });
            }}
          >
            DOWNLOAD PDF
          </Button>
        </MenuItem>
        <MenuItem disableRipple>
          <Button
            fullWidth
            variant="contained"
            startIcon={<TableViewIcon />}
            sx={{ backgroundColor: "#4caf50", color: "#fff" }}
            onClick={() => {
              exportExcel(exportMenu.rows, exportMenu.label);
              setExportMenu({ anchorEl: null, rows: [], label: "" });
            }}
          >
            DOWNLOAD EXCEL
          </Button>
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((old) => ({ ...old, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((old) => ({ ...old, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
